import { Link } from 'react-router-dom';
import ContactForm from '../../components/ContactForm';
import Hero from '../../components/Hero';
import Layout from '../../components/Layout';
import ReviewCard from '../../components/ReviewCard';
import SectionHeading from '../../components/SectionHeading';
import ServiceCard from '../../components/ServiceCard';
import { useSiteContent } from '../../content/ContentContext';

export default function HomePage() {
  const { content, featuredServices } = useSiteContent();
  const { galleryImages, reviews, site } = content;

  return (
    <Layout>
      <Hero />

      <section className="border-y border-slate-200 bg-[#eff9e8]">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-5 text-center sm:grid-cols-2 sm:px-6 lg:grid-cols-5 lg:px-8">
          {site.trustPoints.map((item) => (
            <p key={item} className="text-sm font-bold uppercase tracking-[0.14em] text-[#0d4f12]">
              {item}
            </p>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <SectionHeading
          eyebrow="Our Services"
          title="Complete lawn care and property services built around real local needs"
          description="We provide a wide range of lawn care, landscaping, and property maintenance services for homes and businesses throughout Springfield, Ohio and surrounding areas."
          center
        />
        <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
          {featuredServices.map((service) => (
            <ServiceCard key={service.name} {...service} />
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/services" className="inline-flex rounded-full bg-[#0d4f12] px-6 py-3 font-bold text-white transition hover:bg-[#0a3c0e]">
            View All Services
          </Link>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <SectionHeading eyebrow="Why Choose B&B" title={site.whyChooseTitle} description={site.whyChooseDescription} />
            <div className="grid gap-4 sm:grid-cols-2">
              {site.whyChoosePoints.map((item) => (
                <div key={item} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
                  {item}
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/about" className="inline-flex rounded-full border border-[#0d4f12] px-6 py-3 font-bold text-[#0d4f12] transition hover:bg-[#0d4f12] hover:text-white">
                Learn More About Us
              </Link>
            </div>
          </div>
          <div className="overflow-hidden rounded-[2rem] shadow-xl">
            <img src={site.whyChooseImage} alt={site.whyChooseImageAlt} className="h-full min-h-[360px] w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <SectionHeading eyebrow="Recent Work" title={site.recentWorkTitle} description={site.recentWorkDescription} center />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {galleryImages.slice(0, 6).map((image) => (
            <div key={image.src} className="overflow-hidden rounded-3xl shadow-sm">
              <img src={image.src} alt={image.alt} className="h-72 w-full object-cover transition duration-300 hover:scale-105" />
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/gallery" className="inline-flex rounded-full bg-[#33b300] px-6 py-3 font-bold text-white transition hover:bg-[#289000]">
            View Full Gallery
          </Link>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <SectionHeading eyebrow="Customer Reviews" title={site.reviewsTitle} description={site.reviewsDescription} center />
          <div className="grid gap-6 lg:grid-cols-3">
            {reviews.slice(0, 3).map((review) => (
              <ReviewCard key={review.name} {...review} />
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/reviews" className="inline-flex rounded-full border border-[#0d4f12] px-6 py-3 font-bold text-[#0d4f12] transition hover:bg-[#0d4f12] hover:text-white">
              Read More Reviews
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="rounded-[2rem] bg-[#123018] px-6 py-12 text-white shadow-xl sm:px-10 lg:px-14">
          <SectionHeading eyebrow="Service Area" title={site.serviceAreaTitle} description={site.serviceAreaDescription} light />
          <div className="grid gap-4 text-sm font-semibold uppercase tracking-[0.14em] text-white/90 sm:grid-cols-2 lg:grid-cols-4">
            {site.serviceAreaShortList.map((area) => (
              <div key={area} className="rounded-2xl border border-white/15 bg-white/5 px-5 py-4">{area}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-24">
          <div>
            <SectionHeading eyebrow="Free Estimate" title={site.freeEstimateTitle} description={site.freeEstimateDescription} />
            <div className="space-y-3 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#33b300]">Contact Details</p>
              <p className="text-lg font-black text-slate-900">{site.phoneDisplay}</p>
              <p className="text-base text-slate-700">{site.email}</p>
              <p className="text-base text-slate-700">{site.serviceArea}</p>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>
    </Layout>
  );
}
